import FullPageLoader from "@/components/Common/FullPageLoader";
import type {TokenClaimsSerializer} from "@/generated/DjangoTypes";
import {useUserId} from "@/hooks/useUserHooks";
import {refreshTokensWithLock} from "@/httpClient";
import {locationAtom} from "@/store/jotaiAtoms";
import useTokenStore from "@/store/token";
import {createFileRoute, Outlet, redirect} from "@tanstack/react-router";
import {getDefaultStore} from "jotai";
import {jwtDecode} from "jwt-decode";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({location}) => {
    const isLoggedIn = !!useTokenStore.getState().userId;

    if (!isLoggedIn) {
      const {currentRoute} = getDefaultStore().get(locationAtom);

      getDefaultStore().set(locationAtom, {
        previousRoute: currentRoute,
        currentRoute: `${location.pathname}${location.searchStr}`,
      });

      if (currentRoute === "/register") {
        return;
      }

      if (location.pathname === "/plans") {
        throw redirect({to: "/register", replace: true});
      }

      throw redirect({
        to: "/login",
        search: {redirect: `${location.pathname}${location.searchStr}`},
        replace: true,
      });
    }

    let token = useTokenStore.getState().access;

    // Never authorize off an expired access token: its claims (access_level,
    // agency_ids, sub_brand_ids) may predate a membership/permission change, and
    // child guards decode them synchronously before any 401 would refresh. Force
    // a refresh first so the token the guards read carries current claims from
    // the DB. Only hits the network when the token is actually expired.
    if (token) {
      const {exp} = jwtDecode<TokenClaimsSerializer>(token);

      if (exp * 1000 <= Date.now()) {
        await refreshTokensWithLock();
        token = useTokenStore.getState().access;

        if (!token) {
          throw redirect({
            to: "/login",
            search: {redirect: `${location.pathname}${location.searchStr}`},
            replace: true,
          });
        }
      }
    }
  },
  component: () => {
    const userId = useUserId();

    if (!userId) {
      return <FullPageLoader />;
    }

    return <Outlet />;
  },
});
